import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import { Address } from "@/interfaces/domain/Address";
import { OrderItem, OrderItemQualityEnum } from "@/interfaces/domain/Order";
import { Store } from "@/interfaces/domain/Store";

export interface CheckoutState {
  address: Address;
  store: Store;
  shipmentValue: number;
  items: Map<string, OrderItem>;
  subtotal: number;
  itemsNotFound?: string;
}

export const checkoutInitialState = {
  address: null,
  store: null,
  shipmentValue: null,
  items: new Map<string, OrderItem>(),
  subtotal: 0,
  itemsNotFound: "",
} as CheckoutState;

function updateItems(
  item: OrderItem,
  items: Map<string, OrderItem>,
  action: "increase" | "decrease",
  quantity?: number
) {
  const existentItem = items.get(item.item._id);
  switch (action) {
    case "increase":
      if (existentItem) {
        existentItem.quantidade += quantity ?? 1;
        existentItem.preco_total = existentItem.quantidade * item.preco_unitario;
      } else {
        const unityPrice = item.item.preco.preco_encarte
          ? item.item.preco.preco_encarte
          : item.item.preco.preco_original;
        items.set(item.item._id, {
          ...item,
          quantidade: quantity ?? 1,
          preco_unitario: unityPrice,
          preco_total: unityPrice * (quantity ?? 1),
        });
      }
      break;
    case "decrease":
      if (existentItem && existentItem.quantidade > 1) {
        existentItem.quantidade -=
          quantity >= 0 ? (quantity - existentItem.quantidade <= 0 ? 0 : quantity) : 1;
        existentItem.preco_total = existentItem.quantidade * item.preco_unitario;
      } else {
        items.delete(item.item._id);
      }
      break;
    default:
      break;
  }
}

function calculateSubtotal(items: Map<string, OrderItem>) {
  return Array.from(items.values()).reduce((acc, it) => acc + it.preco_total, 0);
}

export const checkoutSlice = createSlice({
  name: "checkout",
  initialState: checkoutInitialState,
  reducers: {
    SET_ADDRESS: (state, action: PayloadAction<Address>) => ({
      ...state,
      address: action.payload,
      shipmentValue: null,
    }),
    SET_STORE: (state, action: PayloadAction<Store>) => ({
      ...state,
      store: action.payload,
      shipmentValue: null,
    }),
    SET_SHIPMENT_VALUE: (state, action: PayloadAction<number>) => ({
      ...state,
      shipmentValue: action.payload,
    }),
    INCREASE_ITEM: (
      state,
      action: PayloadAction<{
        item: OrderItem;
        quantity?: number;
      }>
    ) => {
      const { item, quantity } = action.payload;
      const items = state.items;
      updateItems(item, items, "increase", quantity);
      state.subtotal = calculateSubtotal(items);
    },
    DECREASE_ITEM: (
      state,
      action: PayloadAction<{
        item: OrderItem;
        quantity?: number;
      }>
    ) => {
      const { item, quantity } = action.payload;
      const items = state.items;
      updateItems(item, items, "decrease", quantity);
      state.subtotal = calculateSubtotal(items);
      if (items.size === 0) {
        state.store = null;
      }
    },
    ADD_ITEMS: (
      state,
      action: PayloadAction<
        {
          item: OrderItem;
          quantity?: number;
        }[]
      >
    ) => {
      const items = state.items;
      action.payload.map(({ item, quantity }) => updateItems(item, items, "increase", quantity));
      state.subtotal = calculateSubtotal(items);
    },
    RESET_STORE_AND_ITENS: (state) => ({
      ...state,
      store: null,
      items: new Map<string, OrderItem>(),
      subtotal: 0,
      shipmentValue: 0,
      itemsNotFound: "",
    }),
    CLEAR_CHECKOUT: () => checkoutInitialState,
    CHANGE_ITEM_INSTRUCTION: (
      state,
      action: PayloadAction<{
        itemId: string;
        instruction: string;
      }>
    ) => {
      const { itemId, instruction } = action.payload;
      const items = state.items;
      const item = items?.get(itemId);
      if (item) {
        item.instrucao = instruction;
      }
    },
    CHANGE_ITEM_QUALITY: (
      state,
      action: PayloadAction<{
        itemId: string;
        quality: OrderItemQualityEnum;
      }>
    ) => {
      const { itemId, quality } = action.payload;
      const items = state.items;
      const item = items?.get(itemId);
      if (item) {
        item.qualidade = quality;
      }
    },
    CHANGE_ITENS_NOT_FOUND: (state, action: PayloadAction<string>) => ({
      ...state,
      itemsNotFound: action.payload,
    }),
  },
});

export const {
  SET_ADDRESS,
  SET_STORE,
  SET_SHIPMENT_VALUE,
  DECREASE_ITEM,
  INCREASE_ITEM,
  ADD_ITEMS,
  RESET_STORE_AND_ITENS,
  CHANGE_ITEM_INSTRUCTION,
  CHANGE_ITEM_QUALITY,
  CHANGE_ITENS_NOT_FOUND,
  CLEAR_CHECKOUT,
} = checkoutSlice.actions;

export default checkoutSlice.reducer;
